import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { HsnSacSearch } from "@/components/HsnSacSearch";

const blank = { name: "", is_service: true, hsn_code: "", gst_rate: 18, unit: "NOS", price: 0 };
const skuFrom = (name) => (name.toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 16) || "ITEM") + "-" + Date.now().toString(36).slice(-4).toUpperCase();

export const QuickAddItemDialog = ({ open, onOpenChange, side = "sales", onCreated }) => {
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(blank); }, [open]);

  const setKind = (svc) => setF({ ...f, is_service: svc, unit: svc ? "NOS" : "PCS", hsn_code: "" });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Enter a name");
    setBusy(true);
    try {
      const priceKey = side === "sales" ? "sale_price" : "purchase_price";
      const r = await api.post("/items", {
        sku: skuFrom(f.name), name: f.name.trim(), hsn_code: f.hsn_code, gst_rate: Number(f.gst_rate),
        unit: f.unit || "NOS", is_service: f.is_service, item_type: f.is_service ? "service" : "goods",
        [priceKey]: Number(f.price) || 0,
      });
      toast.success(`${f.is_service ? "Service" : "Item"} created`);
      onCreated(r.data); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create item"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="quick-add-item-dialog">
        <DialogHeader><DialogTitle className="font-heading">New item / service</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="flex gap-2">
            {[[true, "Service"], [false, "Goods"]].map(([v, l]) => (
              <Button key={l} type="button" size="sm" variant={f.is_service === v ? "default" : "outline"} className="rounded-sm flex-1"
                onClick={() => setKind(v)} data-testid={`quick-item-kind-${l.toLowerCase()}`}>{l}</Button>
            ))}
          </div>
          <div><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-sm mt-1" data-testid="quick-item-name" autoFocus /></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><Label>{f.is_service ? "SAC" : "HSN"} (search)</Label>
              <div className="mt-1"><HsnSacSearch value={f.hsn_code} kind={f.is_service ? "sac" : "hsn"} testId="quick-item-hsn"
                onChange={(v) => setF((p) => ({ ...p, hsn_code: v }))}
                onPick={(r) => setF((p) => ({ ...p, hsn_code: r.code, gst_rate: r.gst_rate ?? p.gst_rate }))} /></div>
            </div>
            <div><Label>GST %</Label><Input type="number" value={f.gst_rate} onChange={(e) => setF({ ...f, gst_rate: e.target.value })} className="rounded-sm mt-1 text-right" data-testid="quick-item-gst" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{side === "sales" ? "Sale price ₹" : "Purchase price ₹"}</Label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="rounded-sm mt-1 text-right" data-testid="quick-item-price" /></div>
            <div><Label>Unit</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value.toUpperCase() })} className="rounded-sm mt-1" data-testid="quick-item-unit" /></div>
          </div>
          <Button onClick={save} disabled={busy} className="w-full rounded-sm bg-accent text-white hover:bg-accent/90" data-testid="quick-item-save">{busy ? "Saving…" : "Create and add to line"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
